import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PhotoCapture from "@/components/PhotoCapture";
import PasswordInput from "@/components/PasswordInput";
import { COUNTRIES, STATES_BY_COUNTRY } from "@/lib/locations";
import CountrySelect from "@/components/CountrySelect";
import WhatsAppOtpDialog from "@/components/WhatsAppOtpDialog";
import { WhatsappLogo } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { detectFromPhone, detectFromEmail } from "@/lib/detectLanguage";

const LOGO = process.env.REACT_APP_LOGO_URL;

const roleLabels = { jugador: "Jugador", profesor: "Profesor" };
const CATEGORIES = [
  { value: "open", label: "Open (mayor nivel)" },
  { value: "1", label: "Primera" },
  { value: "2", label: "Segunda" },
  { value: "3", label: "Tercera" },
  { value: "4", label: "Cuarta" },
  { value: "5", label: "Quinta" },
  { value: "6", label: "Sexta (principiante)" },
];

export default function Register() {
  const [searchParams] = useSearchParams();
  const inviteRole = searchParams.get("rol");
  const invitedName = searchParams.get("name") || "";
  const validRoles = ["jugador", "profesor"];
  const invitedDirectivo = inviteRole === "directivo" || inviteRole === "admin";
  const presetRole = validRoles.includes(inviteRole) ? inviteRole : "jugador";
  const roleLocked = validRoles.includes(inviteRole);

  const [form, setForm] = useState({ name: invitedName, email: "", phone: "", password: "", role: presetRole, category: "", experience_years: "", country: "MX", state: "", sport: "padel", sports: ["padel"] });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPcCode, setShowPcCode] = useState(false);
  const [pcCode, setPcCode] = useState("");
  const [showRefCode, setShowRefCode] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [refValidation, setRefValidation] = useState(null); // { valid, referrer_name } | null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { lang, setLang, userPicked } = useLang();

  // Auto-detect UI language from phone LADA or email TLD as the user types.
  // Honours an explicit choice (`userPicked`) — only adjusts on raw signals.
  useEffect(() => {
    if (userPicked) return;
    const detected = detectFromPhone(form.phone) || detectFromEmail(form.email);
    if (detected && detected !== lang) setLang(detected);
  }, [form.phone, form.email, userPicked, lang, setLang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const showError = (msg) => {
      setError(msg);
      toast.error(msg);
      // Scroll form to top so the error banner is visible on mobile
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
    };
    if (!form.email && !form.phone) {
      showError("Ingresa email o teléfono");
      return;
    }
    if (!photo) {
      showError("Toma una foto para tu perfil");
      return;
    }
    if (!privacyAccepted) {
      showError("Debes aceptar el Aviso de Privacidad para registrarte");
      return;
    }
    if (!form.password || form.password.length < 6) {
      showError("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        password: form.password,
        role: form.role,
        country: form.country || null,
        state: form.state || null,
        sport: form.sport || "padel",
        sports: form.sports && form.sports.length ? form.sports : [form.sport || "padel"],
        privacy_accepted: true,
      };
      if (form.role === "jugador") {
        if (form.category) payload.category = form.category;
        if (form.experience_years !== "") payload.experience_years = parseFloat(form.experience_years);
      } else if (form.role === "profesor") {
        if (form.experience_years !== "") payload.experience_years = parseFloat(form.experience_years);
      }
      if (pcCode.trim()) payload.padel_center_code = pcCode.trim();
      if (refCode.trim()) payload.referral_code = refCode.trim().toUpperCase();
      const userData = await register(payload);
      // Upload photo after register
      if (photo) {
        try {
          const formData = new FormData();
          formData.append("file", photo, "photo.jpg");
          await api.post("/users/photo", formData, { headers: { "Content-Type": "multipart/form-data" } });
        } catch { /* photo upload is non-blocking */ }
      }
      toast.success("Cuenta creada!");
      if (pcCode.trim()) toast.success("Bienvenido a Padel Center", { description: "Acceso al ecosistema activado." });
      if (refCode.trim() && refValidation?.valid) toast.success("¡+10 DYD por usar código de referido! 🎉", { description: `Cortesía de ${refValidation.referrer_name}` });
      navigate("/dashboard");
    } catch (err) {
      showError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-3 py-6 sm:px-6 sm:py-12" data-testid="register-page">
      <div className="w-full max-w-md animate-fade-in">
        <Link to="/" className="flex items-center justify-center gap-3 mb-6 sm:mb-10">
          <img src={LOGO} alt="Logo" className="h-12 w-12 sm:h-14 sm:w-14 rounded-full" />
          <span className="font-['Barlow_Condensed'] font-black text-lg sm:text-xl uppercase tracking-wider">
            Disfruta y Diviertete
          </span>
        </Link>

        <div className="bg-[#1A1A1A] border border-white/10 p-4 sm:p-8" data-testid="register-form-container">
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-1">Crear Cuenta</h1>
          <p className="text-sm text-gray-400 mb-4">Unete a la comunidad de padel</p>

          {/* Quick WhatsApp registration */}
          <button
            type="button"
            onClick={() => setOtpOpen(true)}
            className="w-full bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/40 font-bold uppercase tracking-wide h-11 mb-4 flex items-center justify-center gap-2 text-sm transition-colors"
            data-testid="register-whatsapp-btn"
          >
            <WhatsappLogo size={18} weight="fill" /> Registro rápido con WhatsApp
          </button>
          <div className="relative py-1 mb-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center"><span className="bg-[#1A1A1A] px-2 text-[10px] tracking-widest uppercase text-gray-500">o registro tradicional</span></div>
          </div>

          {/* Photo Capture */}
          <div className="flex justify-center mb-6">
            <PhotoCapture onPhotoTaken={setPhoto} />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre Completo</Label>
              <Input data-testid="register-name-input" value={form.name} onChange={(e) => update("name", e.target.value)}
                placeholder="Tu nombre" className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] placeholder:text-gray-600" required />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Email</Label>
              <Input data-testid="register-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                placeholder="tu@email.com" className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] placeholder:text-gray-600" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Telefono</Label>
              <Input data-testid="register-phone-input" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                placeholder="+52 1234567890" className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] placeholder:text-gray-600" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Pais</Label>
                <CountrySelect
                  value={form.country}
                  onChange={(v) => { update("country", v); update("state", ""); }}
                  testid="register-country-select"
                />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Estado</Label>
                <Select value={form.state} onValueChange={(v) => update("state", v)}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00]" data-testid="register-state-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                    {(STATES_BY_COUNTRY[form.country] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Contrasena</Label>
              <PasswordInput data-testid="register-password-input" value={form.password} onChange={(e) => update("password", e.target.value)}
                placeholder="Minimo 6 caracteres" className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] placeholder:text-gray-600" required />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Rol</Label>
              {roleLocked ? (
                <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00] h-12 flex items-center px-4 font-bold uppercase tracking-wide text-sm" data-testid="register-role-locked">
                  {roleLabels[presetRole] || presetRole}
                  <span className="ml-auto text-xs text-gray-400 normal-case tracking-normal font-normal">Invitacion</span>
                </div>
              ) : (
                <Select value={form.role} onValueChange={(v) => update("role", v)} data-testid="register-role-select">
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]" data-testid="register-role-trigger">
                    <SelectValue placeholder="Selecciona rol" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="jugador" data-testid="role-jugador">Jugador</SelectItem>
                    <SelectItem value="profesor" data-testid="role-profesor">Profesor</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {invitedDirectivo && (
                <p className="text-[11px] text-orange-400 mt-2 leading-relaxed" data-testid="directivo-notice">
                  Las cuentas de Directivo y Administrador las crea unicamente el administrador. Registrate como Jugador o Profesor y pide a tu admin que te asigne el rol.
                </p>
              )}
              <p className="text-[11px] text-gray-500 mt-1.5">
                &iquest;Eres directivo? Contacta al administrador para que te cree la cuenta.
              </p>
            </div>

            {form.role === "jugador" && (
              <>
                <div data-testid="jugador-sport-section">
                  <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Deportes que practicas</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="register-sports-grid">
                    {[
                      { id: "padel",     label: "Pádel",         emoji: "🎾" },
                      { id: "tenis",     label: "Tenis",         emoji: "🎾" },
                      { id: "corredor",  label: "Corredor",      emoji: "🏃" },
                      { id: "balonpie",  label: "Soccer",        emoji: "⚽" },
                      { id: "seleccion", label: "Selección DyD", emoji: "⭐" },
                    ].map((s) => {
                      const on = (form.sports || []).includes(s.id);
                      return (
                        <button key={s.id} type="button"
                          onClick={() => {
                            const arr = new Set(form.sports || []);
                            if (arr.has(s.id)) arr.delete(s.id); else arr.add(s.id);
                            const list = Array.from(arr);
                            update("sports", list);
                            update("sport", list.includes("padel") && list.includes("tenis") ? "ambos" : (list[0] || "padel"));
                          }}
                          className={`px-3 py-3 text-xs uppercase tracking-wider font-bold border transition-colors ${on ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                          data-testid={`register-sport-${s.id}`}
                        >
                          {s.emoji} {s.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">Puedes elegir uno o varios.</p>
                </div>
                <div data-testid="jugador-category-section">
                  <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => update("category", v)}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]" data-testid="jugador-category-trigger">
                      <SelectValue placeholder="Selecciona tu categoria" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value} data-testid={`category-${c.value}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-gray-500 mt-1.5">1ra = mayor nivel competitivo, 6ta = principiante, Open = sin limite.</p>
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Cuanto tiempo llevas jugando (anos)</Label>
                  <Input
                    data-testid="jugador-experience-input"
                    type="number"
                    min="0"
                    max="80"
                    step="0.5"
                    value={form.experience_years}
                    onChange={(e) => update("experience_years", e.target.value)}
                    placeholder="Ej: 2.5"
                    className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] placeholder:text-gray-600"
                  />
                  <p className="text-[11px] text-gray-500 mt-1.5">Usa decimales para meses (0.5 = 6 meses).</p>
                </div>
              </>
            )}

            {form.role === "profesor" && (
              <div data-testid="profesor-experience-section">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Cuanto tiempo llevas dando clinicas (anos)</Label>
                <Input
                  data-testid="profesor-experience-input"
                  type="number"
                  min="0"
                  max="80"
                  step="0.5"
                  value={form.experience_years}
                  onChange={(e) => update("experience_years", e.target.value)}
                  placeholder="Ej: 5"
                  className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] placeholder:text-gray-600"
                />
                <p className="text-[11px] text-gray-500 mt-1.5">Anos de experiencia dando clases/clinicas de padel.</p>
              </div>
            )}
            <div className="bg-[#0A0A0A] border border-white/10 p-4 mt-2" data-testid="privacy-section">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#CCFF00] cursor-pointer shrink-0"
                  data-testid="privacy-checkbox"
                  required
                />
                <span className="text-[12px] text-gray-300 leading-relaxed">
                  He leido y acepto el{" "}
                  <a
                    href="https://customer-assets.emergentagent.com/job_padel-hub-12/artifacts/cyt5pcrq_Aviso_de_privacidad_DYD.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#CCFF00] hover:underline font-bold"
                    data-testid="privacy-link"
                  >
                    Aviso de Privacidad
                  </a>{" "}
                  de Disfruta y Diviertete y autorizo el tratamiento de mis datos personales de acuerdo a sus terminos.
                </span>
              </label>
            </div>

            {/* Optional: Padel Center membership code */}
            <div className="mt-1" data-testid="register-pc-section">
              {!showPcCode ? (
                <button
                  type="button"
                  onClick={() => setShowPcCode(true)}
                  className="w-full text-[10px] tracking-[0.2em] uppercase text-[#CCFF00]/80 hover:text-[#CCFF00] font-bold py-2 border border-dashed border-[#CCFF00]/30 hover:border-[#CCFF00]/60 rounded transition-colors"
                  data-testid="register-pc-toggle"
                >
                  🔒 ¿Eres parte del ecosistema Padel Center? Ingresa el código
                </button>
              ) : (
                <div className="space-y-2 p-3 bg-[#0A1A0A]/60 border border-[#CCFF00]/30 rounded">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00]">Código Padel Center</Label>
                    <button type="button" onClick={() => { setShowPcCode(false); setPcCode(""); }} className="text-[10px] text-gray-500 hover:text-white" data-testid="register-pc-cancel">✕ quitar</button>
                  </div>
                  <Input
                    type="password"
                    value={pcCode}
                    onChange={(e) => setPcCode(e.target.value)}
                    placeholder="Solicítalo al director del club"
                    className="bg-black border-[#CCFF00]/30 text-white text-sm tracking-wider"
                    data-testid="register-pc-code-input"
                  />
                  <p className="text-[9px] text-gray-500 italic">Opcional — si tienes el código, ingresarás automáticamente al ecosistema privado al crear tu cuenta.</p>
                </div>
              )}
            </div>

            {/* Optional: Referral code */}
            <div className="mt-1" data-testid="register-ref-section">
              {!showRefCode ? (
                <button
                  type="button"
                  onClick={() => setShowRefCode(true)}
                  className="w-full text-[10px] tracking-[0.2em] uppercase text-[#CCFF00]/80 hover:text-[#CCFF00] font-bold py-2 border border-dashed border-[#CCFF00]/30 hover:border-[#CCFF00]/60 rounded transition-colors"
                  data-testid="register-ref-toggle"
                >
                  🎁 ¿Tienes un código de referido? Recibe +10 DYD
                </button>
              ) : (
                <div className="space-y-2 p-3 bg-[#0A1A0A]/60 border border-[#CCFF00]/30 rounded">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00]">Código de Referido</Label>
                    <button type="button" onClick={() => { setShowRefCode(false); setRefCode(""); setRefValidation(null); }} className="text-[10px] text-gray-500 hover:text-white" data-testid="register-ref-cancel">✕ quitar</button>
                  </div>
                  <Input
                    type="text"
                    value={refCode}
                    onChange={(e) => { setRefCode(e.target.value.toUpperCase()); setRefValidation(null); }}
                    onBlur={async () => {
                      const code = refCode.trim().toUpperCase();
                      if (!code) { setRefValidation(null); return; }
                      try {
                        const r = await api.get(`/referrals/validate/${encodeURIComponent(code)}`);
                        setRefValidation(r.data);
                      } catch { setRefValidation({ valid: false }); }
                    }}
                    placeholder="REF-XXXXXX"
                    className="bg-black border-[#CCFF00]/30 text-white text-sm tracking-wider font-mono"
                    data-testid="register-ref-code-input"
                  />
                  {refValidation && refValidation.valid && (
                    <p className="text-[10px] text-[#CCFF00] font-bold" data-testid="register-ref-valid">
                      ✓ Código válido — Te invitó {refValidation.referrer_name}. Ambos recibirán +{refValidation.reward_dyd} DYD.
                    </p>
                  )}
                  {refValidation && !refValidation.valid && refCode.trim() && (
                    <p className="text-[10px] text-red-400" data-testid="register-ref-invalid">✗ Código no encontrado</p>
                  )}
                  <p className="text-[9px] text-gray-500 italic">Opcional — Tu amigo y tú recibirán +10 DYD Moneda al completar el registro.</p>
                </div>
              )}
            </div>

            <Button data-testid="register-submit-btn" type="submit" disabled={loading || !privacyAccepted}
              className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide h-12 mt-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? "Creando cuenta..." : "Acepto y me registro"}
            </Button>
          </form>

          <p className="text-sm text-gray-500 mt-6 text-center">
            Ya tienes cuenta?{" "}
            <Link to="/login" className="text-[#CCFF00] hover:underline font-semibold" data-testid="register-login-link">Ingresar</Link>
          </p>
        </div>
      </div>
      <WhatsAppOtpDialog
        open={otpOpen}
        onOpenChange={setOtpOpen}
        defaultPhone={form.phone}
        defaultName={form.name}
      />
    </div>
  );
}
